import { logger } from "@/utils/logger";
/**
 * API Configuration
 *
 * Auto-detects backend URL based on frontend hostname:
 *   - localhost -> http://localhost:8890 (local backend)
 *   - staging.demo.a2zradiology.ai -> staging.api.demo.a2zradiology.ai
 *   - demo.a2zradiology.ai -> api.demo.a2zradiology.ai
 *   - Amplify preview URLs -> mapped based on branch pattern
 *
 * Override with VITE_API_BASE_URL env var for custom setups.
 */

// Backend URLs - single source of truth
const BACKEND_URLS = {
  local: "http://localhost:8890",
  staging: "https://staging.api.demo.a2zradiology.ai",
  production: "https://api.demo.a2zradiology.ai",
} as const;

// Allowed frontend hostnames mapped to environments
// Security: Explicit allowlist prevents malicious domain spoofing
const HOSTNAME_TO_ENV: Record<string, keyof typeof BACKEND_URLS> = {
  // Local development
  localhost: "local",
  "127.0.0.1": "local",

  // Production
  "demo.a2zradiology.ai": "production",

  // Staging
  "staging.demo.a2zradiology.ai": "staging",
};

// Amplify app ID for preview URL detection
const AMPLIFY_APP_ID = "ddkt0ihfyr7b9";

let cachedApiBaseUrl: string | null = null;

/**
 * Derives the API base URL from the current hostname.
 * Uses explicit allowlist for security - unknown hosts fall back to production.
 *
 * Exported for testing.
 */
export function deriveApiBaseUrl(hostname: string, envOverride?: string): string {
  // Priority 1: Environment variable override
  if (envOverride) {
    return envOverride;
  }

  // Priority 2: Explicit hostname match
  const env = HOSTNAME_TO_ENV[hostname];
  if (env) {
    return BACKEND_URLS[env];
  }

  // Priority 3: Amplify preview URLs (*.amplifyapp.com)
  if (hostname.endsWith(".amplifyapp.com")) {
    // staging.{appId}.amplifyapp.com -> staging backend
    if (hostname.startsWith("staging.")) {
      return BACKEND_URLS.staging;
    }
    // main.{appId}.amplifyapp.com or {appId}.amplifyapp.com -> production
    return BACKEND_URLS.production;
  }

  // Fallback: Production (secure default for unknown hosts)
  // Log warning in development to catch misconfigurations
  if (typeof console !== "undefined") {
    console.warn(`[API Config] Unknown hostname "${hostname}", falling back to production`);
  }
  return BACKEND_URLS.production;
}

/**
 * Initialize and cache the API base URL.
 * Call this once at app startup.
 */
export async function getRuntimeConfig(): Promise<{ apiBaseUrl: string }> {
  if (!cachedApiBaseUrl) {
    const envApiUrl = import.meta.env.VITE_API_BASE_URL;
    cachedApiBaseUrl = deriveApiBaseUrl(window.location.hostname, envApiUrl);
    logger.debug(`[API Config] Backend URL: ${cachedApiBaseUrl}`);
  }
  return { apiBaseUrl: cachedApiBaseUrl };
}

/**
 * Get the current API base URL.
 * Must call getRuntimeConfig() first, or this will derive on-demand.
 */
export function getApiBaseUrl(): string {
  if (!cachedApiBaseUrl) {
    const envApiUrl = import.meta.env.VITE_API_BASE_URL;
    // Local Vite uses a same-origin proxy for staging API access. This avoids
    // requiring the deployed API's CORS allowlist to include every developer
    // localhost/127.0.0.1 origin (notably the public Cognito config request).
    if (
      import.meta.env.DEV &&
      import.meta.env.VITE_USE_DEV_API_PROXY === "1" &&
      (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
    ) {
      cachedApiBaseUrl = "";
      return cachedApiBaseUrl;
    }
    cachedApiBaseUrl = deriveApiBaseUrl(window.location.hostname, envApiUrl);
  }
  return cachedApiBaseUrl;
}

/**
 * Get the backend URL with /api prefix.
 */
function getAwsBackendUrl(): string {
  return `${getApiBaseUrl()}/api`;
}

/**
 * Get the DICOM backend URL for DICOM file retrieval.
 */
export function getDicomBaseUrl(): string {
  return getAwsBackendUrl();
}

/**
 * Get the backend URL for study data (study list, study info).
 */
export function getStudyDataBaseUrl(): string {
  return getAwsBackendUrl();
}

/**
 * Get the backend URL for study data using the provided token.
 */
export function getStudyDataBaseUrlWithToken(_token: string | null): string {
  return getAwsBackendUrl();
}

/**
 * Get the backend URL for feedback data.
 */
export function getFeedbackBaseUrl(): string {
  return getAwsBackendUrl();
}

// Export for testing
export const _testExports = {
  BACKEND_URLS,
  HOSTNAME_TO_ENV,
  AMPLIFY_APP_ID,
};
